import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ANY_SPORT_MY_CARDS_BOXES } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { FixtureOverview } from 'components/fixture/FixtureTeamsOverview/FixtureOverview';
import { LastFixtureStoryStep } from 'components/so5/LastFixtureStoryReworked/LastFixtureStoryStep';
import { StoryState } from 'components/so5/LastFixtureStoryReworked/types';
import useClaimRewards from 'hooks/so5/useClaimRewards';

import { useResultsStoryAssets } from '../useResultsStoryAssets';
import { VerificationBanner } from './VerificationBanner';
import { RewardsRecap_so5Fixture } from './__generated__/index.graphql';
import { useRewardsVerification } from './useRewardsVerification';

type CtaProps = {
  box: number;
  card: number;
  onNext?: () => void;
  onClose?: () => Promise<void>;
};
const Cta = ({ box, card, onNext, onClose }: CtaProps) => {
  const { generateSportContextPath } = useSportContext();
  const navigate = useNavigate();

  if (card > 0 && onNext) {
    return (
      <Button size="medium" fullWidth color="secondary" onClick={onNext}>
        <FormattedMessage
          id="RewardsRecap.cta.revealMyCard"
          defaultMessage="Reveal {card, plural, one {card} other {cards}}"
          values={{ card }}
        />
      </Button>
    );
  }
  if (box > 0 && !onNext) {
    return (
      <Button
        size="medium"
        fullWidth
        color="secondary"
        onClick={async () => {
          await onClose?.();
          navigate(generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES));
        }}
      >
        <FormattedMessage
          id="CardRewardsReveal.cta.goToMyBoxes"
          defaultMessage="Go to My Boxes"
        />
      </Button>
    );
  }
  return null;
};

type Props = {
  so5Fixture: RewardsRecap_so5Fixture;
  onNext?: () => void;
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
  from?: StoryState;
};
export const RewardsRecap = ({
  from: fromProp,
  so5Fixture,
  onNext,
  onClose,
}: Props) => {
  const from = useRef(fromProp);
  const { claimableRewardsByType, rewardsByType, verificationNeeded } =
    useRewardsVerification(so5Fixture.mySo5Rewards);

  const [claimed, setClaimed] = useState(false);
  const [claimRewards] = useClaimRewards();

  const boxAndCashrewardIds = [
    ...claimableRewardsByType.box,
    ...claimableRewardsByType.cash,
  ].map(({ id }) => id);
  if (!claimed && boxAndCashrewardIds.length) {
    claimRewards(boxAndCashrewardIds);
    setClaimed(true);
  }

  const { introToRecap, promotionToRecap, recapToEnd, relegationToRecap } =
    useResultsStoryAssets();

  let introVideo = introToRecap;
  if (from.current === 'promotion') {
    introVideo = promotionToRecap;
  }
  if (from.current === 'relegation') {
    introVideo = relegationToRecap;
  }

  return (
    <LastFixtureStoryStep
      so5Fixture={so5Fixture}
      introVideo={introVideo}
      outroVideo={recapToEnd}
      onNext={onNext}
      onClose={onClose}
      type="recap"
      cta={
        rewardsByType.card.length > 0 || rewardsByType.box.length > 0 ? (
          <Cta
            card={rewardsByType.card.length}
            box={rewardsByType.box.length}
            onNext={onNext}
            onClose={onClose}
          />
        ) : null
      }
      banner={<VerificationBanner verificationNeeded={verificationNeeded} />}
    >
      <FixtureOverview
        so5Fixture={so5Fixture}
        onlyRewards
        verificationNeeded={verificationNeeded}
      />
    </LastFixtureStoryStep>
  );
};

RewardsRecap.fragments = {
  so5Fixture: gql`
    fragment RewardsRecap_so5Fixture on So5Fixture {
      slug
      gameWeek
      mySo5Rewards {
        slug
        id
        ...useRewardsVerification_so5Reward
      }
      ...FixtureOverview_so5Fixture
      ...LastFixtureStoryStep_so5Fixture
    }
    ${LastFixtureStoryStep.fragments.so5Fixture}
    ${FixtureOverview.fragments.so5Fixture}
    ${useRewardsVerification.fragments.so5Reward}
  ` as TypedDocumentNode<RewardsRecap_so5Fixture>,
};
